import styled from 'styled-components';
import { TokenName } from '@mews/design-tokens';
import { getToken } from '@optimus-web/common';
import { IconName } from '@optimus-web/icons';
import { Icon } from '../Icon';
import { Stack } from '../Stack';
import { Typography } from '../typography';
import { PasswordRequirementData } from './types';

interface PasswordRequirementListProps {
    value: string;
    requirements: PasswordRequirementData[];
    error?: React.ReactNode;
}

export function PasswordRequirementList({ value, requirements, error }: PasswordRequirementListProps) {
    return (
        <Stack vertical spacing="50">
            {requirements.map((requirement) => (
                <PasswordRequirement status={getPasswordRequirementStatus({ requirement, password: value, error })}>
                    {requirement.label}
                </PasswordRequirement>
            ))}
        </Stack>
    );
}

function getPasswordRequirementStatus({
    requirement,
    password,
    error,
}: {
    requirement: PasswordRequirementData;
    password: string;
    error?: React.ReactNode;
}) {
    // Showing the requirement list hides the default error message
    // We want to show the error message if the password is empty and some external validation has occurred
    // TODO: This should be handled in a more generic way

    if (!password && !error) {
        return 'empty';
    }

    return requirement.validate(password) ? 'valid' : 'invalid';
}

interface PasswordRequirementProps {
    status: PasswordRequirementStatus;
}

type PasswordRequirementStatus = 'valid' | 'invalid' | 'empty';
type PasswordRequirementColor = TokenName.textAlertSuccess | TokenName.textAlertDanger | TokenName.textStaticSecondary;

const passwordRequirementsVariantProps: Record<
    PasswordRequirementStatus,
    { icon: IconName; color: PasswordRequirementColor }
> = {
    valid: {
        icon: IconName.CheckCircle,
        color: TokenName.textAlertSuccess,
    },
    invalid: {
        icon: IconName.AlertCircle,
        color: TokenName.textAlertDanger,
    },
    empty: {
        icon: IconName.RadioEmpty,
        color: TokenName.textStaticSecondary,
    },
};

function PasswordRequirement({ status, children }: React.PropsWithChildren<PasswordRequirementProps>) {
    const { icon, color } = passwordRequirementsVariantProps[status];
    return (
        <PasswordRequirementContainer spacing="100" color={color} itemAlignment="center">
            <Icon value={icon} size="small" />
            <PasswordRequirementTypography as="span" textStyle="bodyMedium">
                {children}
            </PasswordRequirementTypography>
        </PasswordRequirementContainer>
    );
}

const PasswordRequirementContainer = styled(Stack)<{
    color: PasswordRequirementColor;
}>`
    color: ${({ color }) => getToken(color)};
`;

const PasswordRequirementTypography = styled(Typography)`
    color: currentColor;
`;
